import { supabase } from '../../lib/supabase';
export class SalesDealsService {
    /**
     * Get all deals for an organization with optional filtering
     */
    async getDeals(organizationId, filters) {
        try {
            let query = supabase
                .from('sales_deals')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('is_active', true)
                .order('created_at', { ascending: false });
            // Apply filters
            if (filters?.search) {
                query = query.or(`title.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
            }
            if (filters?.stage && filters.stage.length > 0) {
                query = query.in('stage', filters.stage);
            }
            if (filters?.tags && filters.tags.length > 0) {
                query = query.contains('tags', filters.tags);
            }
            if (filters?.ownerId) {
                query = query.eq('owner_id', filters.ownerId);
            }
            if (filters?.valueRange) {
                if (filters.valueRange.min !== undefined) {
                    query = query.gte('value', filters.valueRange.min);
                }
                if (filters.valueRange.max !== undefined) {
                    query = query.lte('value', filters.valueRange.max);
                }
            }
            if (filters?.isStarred !== undefined) {
                query = query.eq('is_starred', filters.isStarred);
            }
            if (filters?.dateRange) {
                query = query
                    .gte('expected_close_date', filters.dateRange.start.toISOString())
                    .lte('expected_close_date', filters.dateRange.end.toISOString());
            }
            const { data, error } = await query;
            if (error) {
                console.error('Error fetching deals:', error);
                throw error;
            }
            return this.transformDeals(data || []);
        }
        catch (error) {
            console.error('Error in getDeals:', error);
            return [];
        }
    }
    /**
     * Get a single deal by ID with contact info
     */
    async getDeal(dealId) {
        try {
            const { data: deal, error: dealError } = await supabase
                .from('sales_deals')
                .select('*')
                .eq('id', dealId)
                .single();
            if (dealError) {
                console.error('Error fetching deal:', dealError);
                return null;
            }
            // Fetch contact if available
            let contact = null;
            if (deal.contact_id) {
                const { data: contactData } = await supabase
                    .from('sales_contacts')
                    .select('*')
                    .eq('id', deal.contact_id)
                    .single();
                contact = contactData;
            }
            // Fetch activities
            const { data: activities } = await supabase
                .from('sales_activities')
                .select('*')
                .eq('deal_id', dealId)
                .order('created_at', { ascending: false })
                .limit(50);
            return {
                ...this.transformDeal(deal),
                contact: contact || undefined,
                activities: activities || []
            };
        }
        catch (error) {
            console.error('Error in getDeal:', error);
            return null;
        }
    }
    /**
     * Create a new deal
     */
    async createDeal(organizationId, userId, request) {
        try {
            const { data, error } = await supabase
                .from('sales_deals')
                .insert({
                organization_id: organizationId,
                contact_id: request.contactId,
                title: request.title,
                value: request.value,
                stage: request.stage,
                expected_close_date: request.expectedCloseDate?.toISOString(),
                notes: request.notes,
                created_by: userId,
                owner_id: userId,
                assigned_to: userId
            })
                .select()
                .single();
            if (error) {
                console.error('Error creating deal:', error);
                throw error;
            }
            return this.transformDeal(data);
        }
        catch (error) {
            console.error('Error in createDeal:', error);
            return null;
        }
    }
    /**
     * Update an existing deal
     */
    async updateDeal(request) {
        try {
            const { id, ...updates } = request;
            const updateData = {};
            if (updates.title !== undefined)
                updateData.title = updates.title;
            if (updates.contactId !== undefined)
                updateData.contact_id = updates.contactId;
            if (updates.value !== undefined)
                updateData.value = updates.value;
            if (updates.stage !== undefined)
                updateData.stage = updates.stage;
            if (updates.expectedCloseDate !== undefined) {
                updateData.expected_close_date = updates.expectedCloseDate?.toISOString();
            }
            if (updates.notes !== undefined)
                updateData.notes = updates.notes;
            const { data, error } = await supabase
                .from('sales_deals')
                .update(updateData)
                .eq('id', id)
                .select()
                .single();
            if (error) {
                console.error('Error updating deal:', error);
                throw error;
            }
            return this.transformDeal(data);
        }
        catch (error) {
            console.error('Error in updateDeal:', error);
            return null;
        }
    }
    /**
     * Update deal stage
     */
    async updateStage(dealId, stage) {
        try {
            const updateData = { stage };
            // If closed-won or closed-lost, set actual_close_date
            if (stage === 'closed-won' || stage === 'closed-lost') {
                updateData.actual_close_date = new Date().toISOString();
            }
            const { error } = await supabase
                .from('sales_deals')
                .update(updateData)
                .eq('id', dealId);
            if (error) {
                console.error('Error updating deal stage:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in updateStage:', error);
            return false;
        }
    }
    /**
     * Delete a deal (soft delete)
     */
    async deleteDeal(dealId) {
        try {
            const { error } = await supabase
                .from('sales_deals')
                .update({ is_active: false })
                .eq('id', dealId);
            if (error) {
                console.error('Error deleting deal:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in deleteDeal:', error);
            return false;
        }
    }
    /**
     * Toggle star status
     */
    async toggleStar(dealId, isStarred) {
        try {
            const { error } = await supabase
                .from('sales_deals')
                .update({ is_starred: isStarred })
                .eq('id', dealId);
            if (error) {
                console.error('Error toggling star:', error);
                return false;
            }
            return true;
        }
        catch (error) {
            console.error('Error in toggleStar:', error);
            return false;
        }
    }
    /**
     * Get pipeline statistics
     */
    async getPipelineStats(organizationId) {
        try {
            const { data: deals, error } = await supabase
                .from('sales_deals')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('is_active', true);
            if (error) {
                console.error('Error fetching pipeline stats:', error);
                throw error;
            }
            const allDeals = deals || [];
            const wonDeals = allDeals.filter(d => d.stage === 'closed-won');
            const lostDeals = allDeals.filter(d => d.stage === 'closed-lost');
            const activeDeals = allDeals.filter(d => d.stage !== 'closed-won' && d.stage !== 'closed-lost');
            const totalValue = activeDeals.reduce((sum, d) => sum + (d.value || 0), 0);
            const averageDealSize = activeDeals.length > 0 ? totalValue / activeDeals.length : 0;
            // Calculate average time to close for won deals
            const closedDealsWithDates = wonDeals.filter(d => d.actual_close_date && d.created_at);
            const averageTimeToClose = closedDealsWithDates.length > 0
                ? closedDealsWithDates.reduce((sum, d) => {
                    const created = new Date(d.created_at).getTime();
                    const closed = new Date(d.actual_close_date).getTime();
                    return sum + (closed - created) / (1000 * 60 * 60 * 24); // Convert to days
                }, 0) / closedDealsWithDates.length
                : 0;
            // Calculate win rate
            const totalClosedDeals = wonDeals.length + lostDeals.length;
            const winRate = totalClosedDeals > 0 ? (wonDeals.length / totalClosedDeals) * 100 : 0;
            // Group deals by stage
            const dealsByStage = allDeals.reduce((acc, deal) => {
                const existing = acc.find(s => s.stage === deal.stage);
                if (existing) {
                    existing.count++;
                    existing.value += deal.value || 0;
                }
                else {
                    acc.push({
                        stage: deal.stage,
                        count: 1,
                        value: deal.value || 0
                    });
                }
                return acc;
            }, []);
            return {
                totalValue,
                totalDeals: activeDeals.length,
                wonDeals: wonDeals.length,
                lostDeals: lostDeals.length,
                averageDealSize,
                averageTimeToClose,
                winRate,
                dealsByStage
            };
        }
        catch (error) {
            console.error('Error in getPipelineStats:', error);
            return {
                totalValue: 0,
                totalDeals: 0,
                wonDeals: 0,
                lostDeals: 0,
                averageDealSize: 0,
                averageTimeToClose: 0,
                winRate: 0,
                dealsByStage: []
            };
        }
    }
    /**
     * Get deals by stage for kanban view
     */
    async getDealsByStage(organizationId) {
        try {
            const { data, error } = await supabase
                .from('sales_deals')
                .select('*')
                .eq('organization_id', organizationId)
                .eq('is_active', true)
                .order('created_at', { ascending: false });
            if (error) {
                console.error('Error fetching deals by stage:', error);
                return {};
            }
            const deals = this.transformDeals(data || []);
            // Group by stage
            return deals.reduce((acc, deal) => {
                if (!acc[deal.stage]) {
                    acc[deal.stage] = [];
                }
                acc[deal.stage].push(deal);
                return acc;
            }, {});
        }
        catch (error) {
            console.error('Error in getDealsByStage:', error);
            return {};
        }
    }
    /**
     * Subscribe to real-time deal changes
     */
    subscribeToDeals(organizationId, callback) {
        return supabase
            .channel(`sales_deals:${organizationId}`)
            .on('postgres_changes', {
            event: '*',
            schema: 'public',
            table: 'sales_deals',
            filter: `organization_id=eq.${organizationId}`
        }, (payload) => {
            if (payload.new) {
                callback(this.transformDeal(payload.new));
            }
        })
            .subscribe();
    }
    /**
     * Transform database row to SalesDeal type
     */
    transformDeal(row) {
        return {
            id: row.id,
            organizationId: row.organization_id,
            contactId: row.contact_id,
            title: row.title,
            description: row.description,
            value: row.value || 0,
            currency: row.currency || 'USD',
            stage: row.stage,
            probability: row.probability || 0,
            expectedCloseDate: row.expected_close_date ? new Date(row.expected_close_date) : undefined,
            actualCloseDate: row.actual_close_date ? new Date(row.actual_close_date) : undefined,
            lossReason: row.loss_reason,
            projectId: row.project_id,
            missionId: row.mission_id,
            ownerId: row.owner_id,
            assignedTo: row.assigned_to,
            tags: row.tags || [],
            customFields: row.custom_fields || {},
            isActive: row.is_active,
            isStarred: row.is_starred || false,
            notes: row.notes,
            createdBy: row.created_by,
            createdAt: new Date(row.created_at),
            updatedAt: new Date(row.updated_at)
        };
    }
    /**
     * Transform multiple database rows
     */
    transformDeals(rows) {
        return rows.map(row => this.transformDeal(row));
    }
}
// Export singleton instance
export const salesDealsService = new SalesDealsService();
